import React, { useState } from "react";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, ChevronDown, ChevronUp, Gift, Star, Trophy, Plus, Zap } from "lucide-react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import AwardsSection from "@/components/trust/AwardsSection";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import Testimonials from "@/components/Testimonials";
import { usePostHog } from "@/hooks/usePostHog";

// Component for the pricing page
const Pricing = () => {
  const { posthog } = usePostHog();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  
  const toggleFaq = (index: number) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };
  
  // Demo booking function
  const handleBookDemo = (section: string, buttonText: string) => {
    posthog?.capture('book_demo_clicked', {
      page: '/pricing',
      section: section,
      button_text: buttonText,
      url: window.location.href
    });
    setIsDemoModalOpen(true);
  };
  
  // FAQ data
  const faqs = [
    {
      question: "Can I upgrade later?",
      answer: "Yes! You can start with any plan and upgrade at any time as your organization's needs evolve. Your existing data and settings will transfer seamlessly to your new plan."
    },
    {
      question: "How does Assembly handle rewards?",
      answer: "Assembly offers a global reward catalog with diverse options including gift cards, merchandise, charitable donations, and more. You can also add custom rewards specific to your organization. Rewards can be redeemed through points earned via recognition."
    },
    {
      question: "Can I use add-ons with any plan?",
      answer: "Yes, all add-ons are compatible with the Empower plan, giving you the flexibility to customize your platform to your organization's specific needs."
    },
    {
      question: "What integrations are supported?",
      answer: "Assembly integrates with 100+ tools including Slack, Microsoft Teams, BambooHR, Workday, Google Workspace, Zoom, and many more. Our API also enables custom integrations for enterprise customers."
    }
  ];
  

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Pricing | Assembly - Employee Recognition Platform</title>
        <meta 
          name="description" 
          content="Choose the Assembly plan that's right for your organization. Start simple and customize as you grow with our flexible recognition, rewards, and engagement solutions." 
        />
        <meta name="keywords" content="employee recognition pricing, employee engagement pricing, recognition software cost" />
      </Helmet>

      <Navbar />

      <main className="pt-0">
        {/* Hero Section - Extended behind navbar */}
        <section className="py-3 md:py-5 px-4 bg-gradient-to-b from-blue-50 to-white pt-20 lg:pt-24">
          <div className="container mx-auto max-w-5xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Pick a plan that powers your culture</h1>
            <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
              Start simple, customize as you grow — your way.
            </p>
            
            
            
          </div>
        </section>


        {/* Plan Cards - Adjusted to show more on initial load */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Empower Plan */}
              <Card className="border-green-600/20 hover:border-green-600/50 transition-all shadow-lg hover:shadow-xl scale-105 relative flex flex-col">
                <div className="absolute -top-4 left-0 right-0 flex justify-center">
                  <div className="bg-green-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                </div>
                <CardHeader className="pb-4 border-b">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-3">
                      <Zap className="text-white w-6 h-6" />
                    </div>
                    <CardTitle className="text-2xl text-green-800">Empower</CardTitle>
                  </div>
                  <CardDescription className="text-gray-600">
                    Add peer-to-peer recognition, points, and AI-powered insights for bottom-up culture building.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 flex-grow">
                  <div className="mb-6">
                    <p className="text-3xl font-bold text-gray-900">$3</p>
                    <p className="text-sm text-gray-500">per user / month (billed yearly)</p>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <p className="font-medium text-gray-900">Key Features:</p>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-green-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Milestone celebrations, Announcements, Awards & Challenges</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-green-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Global reward catalog</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-green-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Peer-to-peer recognition</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-green-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Points and budget controls</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-green-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Dora Hub (AI insights, nudges, trends)</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-green-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Advanced people analytics</p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-2">
                  <Button 
                    onClick={() => handleBookDemo('empower_plan', 'Get a Demo')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium"
                  >
                    Get a Demo
                  </Button>
                </CardFooter>
              </Card>

              {/* Custom Plan */}
              <Card className="border-red-600/20 hover:border-red-600/50 transition-all shadow-md hover:shadow-xl flex flex-col">
                <CardHeader className="pb-4 border-b">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-3">
                      <Star className="text-white w-6 h-6" />
                    </div>
                    <CardTitle className="text-2xl text-red-800">Enterprise</CardTitle>
                  </div>
                  <CardDescription className="text-gray-600">
                    Let teams with unique needs build exactly what they want.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 flex-grow">
                  <div className="space-y-3 mb-6 flex flex-col justify-center">
                    <div className="flex items-start">
                      <CheckCircle2 className="text-red-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Advanced security & compliance options</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-red-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Custom integrations</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-red-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Enterprise-grade support</p>
                    </div>
                    <div className="flex items-start">
                      <CheckCircle2 className="text-red-600 w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">Tailored implementation</p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-2">
                  <Button 
                    onClick={() => handleBookDemo('enterprise_plan', 'Get a Demo')}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium"
                  >
                    Get a Demo
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section>

        {/* Trust Section - Added before feature comparison to build credibility */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="flex justify-center mb-4">
                  <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-2 rounded-full shadow-lg flex items-center gap-2">
                    <Trophy className="h-5 w-5" />
                    <span className="font-bold text-sm">AWARD WINNING SOFTWARE</span>
                    <Trophy className="h-5 w-5" />
                  </div>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Highly Rated Culture Platform</h2>
                
              </div>
              <div className="mb-8">
                <AwardsSection />
              </div>
            {/* Reviews Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-blue-100 hover:border-blue-200 group">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-white p-3 rounded-xl shadow-sm">
                    <img 
                      src="/lovable-uploads/e68d106f-69a9-45e8-8624-7f09b1ae1e6f.png" 
                      alt="G2 Logo" 
                      className="h-8 w-8 object-contain"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2 text-center">4.9/5</p>
                <p className="text-gray-600 text-center mb-4">Based on 2400+ reviews</p>
                <a
                  href="https://www.g2.com/products/assembly-2021-12-09/reviews"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium text-center transition-colors group-hover:shadow-md"
                >
                  Read G2 Reviews
                </a>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-purple-100 hover:border-purple-200 group">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-white p-3 rounded-xl shadow-sm">
                    <span className="text-xl font-bold text-purple-600">Gartner</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2 text-center">4.9/5</p>
                <p className="text-gray-600 text-center mb-4">Based on 200+ reviews</p>
                <a
                  href="https://www.gartner.com/reviews/vendor/assembly"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium text-center transition-colors group-hover:shadow-md"
                >
                  Read Gartner Reviews
                </a>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-orange-100 hover:border-orange-200 group">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-white p-3 rounded-xl shadow-sm">
                    <img 
                      src="/lovable-uploads/95d3c1cd-2844-475f-9ab5-54151ecb5f86.png" 
                      alt="Capterra Logo" 
                      className="h-8 w-8 object-contain"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-1 mb-4">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                  <div className="relative">
                    <Star className="h-5 w-5 text-gray-300" />
                    <Star className="h-5 w-5 text-yellow-400 fill-yellow-400 absolute top-0 left-0" style={{clipPath: 'inset(0 30% 0 0)'}} />
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2 text-center">4.7/5</p>
                <p className="text-gray-600 text-center mb-4">Based on 90+ reviews</p>
                <a
                  href="https://www.capterra.com/p/181224/Assembly/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg font-medium text-center transition-colors group-hover:shadow-md"
                >
                  Read Capterra Reviews
                </a>
              </div>
            </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <Testimonials />

        {/* FAQs Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="border rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => toggleFaq(index)}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                    {expandedFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  
                  {expandedFaq === index && (
                    <p className="mt-4 text-gray-600">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section - Blue background, replaces the duplicated yellow CTA section */}
        <section className="py-16 px-4 bg-blue-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to build a culture people love?</h2>
            <p className="text-xl mb-8 opacity-90">Let's tailor a plan that fits your goals.</p>
            <Button 
              onClick={() => handleBookDemo('final_cta', 'Book a Demo')}
              className="bg-white text-blue-700 hover:bg-gray-100 px-8 py-6 rounded-lg text-lg font-medium"
            >
              Book a Demo
            </Button>
          </div>
        </section>
      </main>

      <Footer />
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </div>
  );
};

export default Pricing;
